import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { 
  Users, 
  UserPlus,
  Shield,
  ShieldCheck,
  UserX,
  UserCheck,
  Mail,
  Trash2,
  KeyRound
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";

export default function TeamPage({ user }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [allowlist, setAllowlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("smm");
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
  const [pwdTargetEmail, setPwdTargetEmail] = useState("");
  const [pwdValue, setPwdValue] = useState("");
  const [pwdSubmitting, setPwdSubmitting] = useState(false);

  const isDirettore = user?.role === "direttore_tecnico" || user?.role === "direttore_commerciale" || !!user?.is_admin;
  const isAdminOwner = user?.email?.toLowerCase() === "info@diecixcento.com" || user?.role === "amm" || !!user?.is_admin;
  const isPM = user?.role === "pm" || isDirettore || isAdminOwner || (user?.secondary_roles || []).includes("pm") || !!user?.is_admin;

  useEffect(() => {
    if (!isPM) {
      navigate("/dashboard");
      return;
    }
    fetchData();
  }, [isPM, navigate]);

  const fetchData = async () => {
    try {
      const [usersRes, allowlistRes] = await Promise.all([
        apiFetch("/users"),
        apiFetch("/allowlist")
      ]);

      if (usersRes.ok) setUsers(await usersRes.json());
      if (allowlistRes.ok) setAllowlist(await allowlistRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToAllowlist = async () => {
    if (!newEmail) {
      toast.error("Inserisci un'email");
      return;
    }

    try {
      const response = await apiFetch("/allowlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: newEmail, role: newRole })
      });

      if (response.ok) {
        toast.success("Email aggiunta alla allowlist");
        setNewEmail("");
        setNewRole("smm");
        setAddDialogOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'aggiunta");
      }
    } catch (error) {
      toast.error("Errore nell'aggiunta alla allowlist");
    }
  };

  const handleRemoveFromAllowlist = async (email) => {
    try {
      const response = await apiFetch(`/allowlist/${encodeURIComponent(email)}`, {
        method: "DELETE"
      });

      if (response.ok) {
        toast.success("Email rimossa dalla allowlist");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  const openPwdDialog = (email) => {
    setPwdTargetEmail(email);
    setPwdValue("");
    setPwdDialogOpen(true);
  };

  const handleSetPassword = async () => {
    if (!pwdValue || pwdValue.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }
    setPwdSubmitting(true);
    try {
      const response = await apiFetch(
        `/allowlist/${encodeURIComponent(pwdTargetEmail)}/set-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: pwdValue }),
        }
      );
      if (response.ok) {
        toast.success(`Password impostata per ${pwdTargetEmail}. Condividi le credenziali in modo sicuro.`);
        setPwdDialogOpen(false);
        setPwdValue("");
        setPwdTargetEmail("");
        fetchData();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Errore impostazione password");
      }
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setPwdSubmitting(false);
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const response = await apiFetch(`/users/${userId}/toggle-active`, {
        method: "PATCH"
      });

      if (response.ok) {
        toast.success("Stato utente aggiornato");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      const response = await apiFetch(`/users/${userId}/role?role=${newRole}`, {
        method: "PATCH"
      });

      if (response.ok) {
        toast.success("Ruolo aggiornato");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento del ruolo");
    }
  };

  const handleToggleSecondaryRole = async (u, role) => {
    const current = u.secondary_roles || [];
    const next = current.includes(role)
      ? current.filter(r => r !== role)
      : [...current.filter(r => r !== u.role), role];
    try {
      const res = await apiFetch(`/users/${u.user_id}/secondary-roles`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roles: next })
      });
      if (res.ok) {
        toast.success("Ruoli aggiuntivi aggiornati");
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore aggiornamento ruoli");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    }
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Eliminare definitivamente l'utente "${u.name}"?\n\nQuesta operazione rimuoverà:\n- L'account utente\n- Tutti gli assegnamenti su clienti\n- Sessioni e notifiche\n- L'autorizzazione di accesso\n\nL'operazione è irreversibile.`)) return;
    try {
      const res = await apiFetch(`/users/${u.user_id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Utente eliminato");
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore eliminazione");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    }
  };

  if (!isPM) {
    return null;
  }

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-48"></div>
        <div className="h-64 bg-muted rounded-xl"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
            Team
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestisci utenti e permessi di accesso
          </p>
        </div>

        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-user-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Aggiungi Utente
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Aggiungi alla Allowlist</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@esempio.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  data-testid="new-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Ruolo</Label>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger data-testid="new-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="smm">SMM</SelectItem>
                    <SelectItem value="pm">Project Manager</SelectItem>
                    <SelectItem value="amm">Amministrazione</SelectItem>
                    <SelectItem value="videomaker">Content Creator</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="filmmaker">Videomaker</SelectItem>
                    <SelectItem value="commerciale">Commerciale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleAddToAllowlist}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="confirm-add-user-btn"
              >
                Aggiungi
              </Button>
              <p className="text-xs text-muted-foreground">
                L&apos;utente potrà accedere con Google oppure con email + password (dopo che l&apos;avrai impostata dal pulsante &quot;Imposta password&quot;).
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Active Users */}
      <Card className="border border-slate-200" data-testid="users-section">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Users className="w-5 h-5" />
            Utenti Attivi ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              Nessun utente ha ancora effettuato l'accesso
            </p>
          ) : (
            <div className="space-y-3">
              {users.map((u, index) => (
                <div
                  key={u.user_id}
                  className={`
                    flex items-center justify-between p-4 border rounded-lg
                    ${u.is_active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-60"}
                  `}
                  data-testid={`user-row-${index}`}
                >
                  <div className="flex items-center gap-3">
                    {u.picture ? (
                      <img
                        src={u.picture}
                        alt={u.name}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-indigo-600 font-medium">
                          {u.name?.charAt(0) || "?"}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-foreground">{u.name}</p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-3">
                    <Select
                      value={u.role}
                      onValueChange={(value) => handleChangeRole(u.user_id, value)}
                      disabled={u.user_id === user?.user_id}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="smm">
                          <span className="flex items-center gap-1">
                            <Shield className="w-3 h-3" /> SMM
                          </span>
                        </SelectItem>
                        <SelectItem value="pm">
                          <span className="flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" /> PM
                          </span>
                        </SelectItem>
                        <SelectItem value="amm">
                          <span className="flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" /> AMM
                          </span>
                        </SelectItem>
                        <SelectItem value="videomaker">
                          <span className="flex items-center gap-1">
                            <Shield className="w-3 h-3" /> Content Creator
                          </span>
                        </SelectItem>
                        <SelectItem value="editor">
                          <span className="flex items-center gap-1">
                            <Shield className="w-3 h-3" /> Editor
                          </span>
                        </SelectItem>
                        <SelectItem value="filmmaker">
                          <span className="flex items-center gap-1">
                            <Shield className="w-3 h-3" /> Videomaker
                          </span>
                        </SelectItem>
                        <SelectItem value="commerciale">
                          <span className="flex items-center gap-1">
                            <Shield className="w-3 h-3" /> Commerciale
                          </span>
                        </SelectItem>
                        <SelectItem value="direttore_tecnico">
                          <span className="flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" /> Direttore Tecnico
                          </span>
                        </SelectItem>
                        <SelectItem value="direttore_commerciale">
                          <span className="flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" /> Direttore Commerciale
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    {u.user_id !== user?.user_id && (
                      <>
                      <Button
                        variant={u.is_active ? "outline" : "default"}
                        size="sm"
                        onClick={() => handleToggleActive(u.user_id)}
                        data-testid={`toggle-active-${index}`}
                      >
                        {u.is_active ? (
                          <>
                            <UserX className="w-4 h-4 mr-1" />
                            Disabilita
                          </>
                        ) : (
                          <>
                            <UserCheck className="w-4 h-4 mr-1" />
                            Abilita
                          </>
                        )}
                      </Button>
                      {!u.is_active && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                          onClick={() => handleDeleteUser(u)}
                          data-testid={`delete-user-${index}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Elimina
                        </Button>
                      )}
                      </>
                    )}
                    </div>
                    
                    {/* Ruoli secondari */}
                    {u.user_id !== user?.user_id && (
                      <div className="flex items-center gap-2 flex-wrap justify-end" data-testid={`secondary-roles-${index}`}>
                        <span className="text-xs text-muted-foreground">Ruoli aggiuntivi:</span>
                        {["pm","smm","videomaker","editor","filmmaker","amm","commerciale","direttore_tecnico","direttore_commerciale"].filter(r => r !== u.role).map(r => {
                          const active = (u.secondary_roles || []).includes(r);
                          const labels = {pm:"PM", smm:"SMM", videomaker:"Content Creator", editor:"Editor", filmmaker:"Videomaker", amm:"AMM", commerciale:"Commerciale", direttore_tecnico:"Dir. Tecnico", direttore_commerciale:"Dir. Commerciale"};
                          return (
                            <button
                              key={r}
                              type="button"
                              onClick={() => handleToggleSecondaryRole(u, r)}
                              data-testid={`secondary-role-${r}-${index}`}
                              className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                                active
                                  ? "bg-indigo-600 text-white border-indigo-600"
                                  : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"
                              }`}
                            >
                              {labels[r]}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Allowlist */}
      <Card className="border border-slate-200" data-testid="allowlist-section">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Allowlist Email ({allowlist.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {allowlist.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              Nessuna email nella allowlist. Aggiungi email per permettere l'accesso.
            </p>
          ) : (
            <div className="space-y-2">
              {allowlist.map((entry, index) => {
                const hasLoggedIn = users.some(u => u.email?.toLowerCase() === entry.email?.toLowerCase());
                
                return (
                  <div
                    key={entry.allowlist_id || index}
                    className="flex items-center justify-between p-3 border border-slate-200 rounded-lg"
                    data-testid={`allowlist-row-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <Mail className="w-4 h-4 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{entry.email}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <Badge variant="secondary" className="text-xs">
                            {entry.role === "pm" ? "Project Manager" : entry.role === "amm" ? "Amministrazione" : entry.role === "videomaker" ? "Content Creator" : entry.role === "editor" ? "Editor" : entry.role === "filmmaker" ? "Videomaker" : entry.role === "commerciale" ? "Commerciale" : "SMM"}
                          </Badge>
                          {hasLoggedIn && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                              Ha effettuato accesso
                            </Badge>
                          )}
                          {entry.has_password && (
                            <Badge className="bg-indigo-100 text-indigo-700 text-xs">
                              <KeyRound className="w-3 h-3 mr-1" />
                              Password impostata
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                        onClick={() => openPwdDialog(entry.email)}
                        data-testid={`set-password-${index}`}
                      >
                        <KeyRound className="w-3.5 h-3.5 mr-1" />
                        {entry.has_password ? "Reimposta" : "Imposta password"}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleRemoveFromAllowlist(entry.email)}
                        data-testid={`remove-allowlist-${index}`}
                      >
                        Rimuovi
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Set Password Dialog */}
      <Dialog open={pwdDialogOpen} onOpenChange={setPwdDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="set-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              Imposta Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Email</Label>
              <p className="text-sm font-medium text-foreground">{pwdTargetEmail}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">Nuova password (min 6 caratteri)</Label>
              <Input
                id="new-password"
                type="text"
                placeholder="Es. Diecix2026!"
                value={pwdValue}
                onChange={(e) => setPwdValue(e.target.value)}
                data-testid="new-password-input"
                autoComplete="new-password"
              />
              <p className="text-xs text-muted-foreground">
                Condividi questa password con il membro del team in modo sicuro. Potrà cambiarla dopo il primo accesso.
              </p>
            </div>
            <Button
              onClick={handleSetPassword}
              disabled={pwdSubmitting}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="confirm-set-password-btn"
            >
              {pwdSubmitting ? "Salvataggio..." : "Salva password"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
